import { z } from 'zod'
import { Environment, IsoDateTime } from './common'

// HTTP API connector presets (entry 33): one node kind for vendors and for our own services.

export const AuthKind = z.enum(['none', 'api_key', 'bearer', 'basic', 'oauth2_client', 'hmac'])
export type AuthKind = z.infer<typeof AuthKind>

export const HttpMethod = z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE'])

export const RetryRule = z.object({
  attempts: z.number().int().min(1).max(5),
  backoffMs: z.number().int().min(0),
  on: z.array(z.enum(['timeout', '5xx', '429', 'network'])),
})
export type RetryRule = z.infer<typeof RetryRule>

/** How a reply becomes an outcome. First matching rule wins; `fallback` when none matches. */
export const OutcomeRule = z.object({
  outcome: z.string(),
  status: z
    .union([z.number().int(), z.string()])
    .optional()
    .describe('HTTP status or a class like "2xx", "5xx"'),
  path: z.string().optional().describe('JSON path in the reply body, e.g. $.status'),
  equals: z.union([z.string(), z.number(), z.boolean()]).optional(),
})
export type OutcomeRule = z.infer<typeof OutcomeRule>

export const OutcomeMap = z.object({
  rules: z.array(OutcomeRule),
  fallback: z.string().default('error'),
  timedOut: z.string().default('timed_out'),
})
export type OutcomeMap = z.infer<typeof OutcomeMap>

export const PresetField = z.object({
  name: z.string(),
  type: z.enum(['string', 'number', 'boolean', 'object']),
  required: z.boolean().default(false),
  pii: z.boolean().default(false),
  description: z.string().optional(),
})

export const ConnectorPreset = z.object({
  id: z.string(),
  workspaceId: z.string().nullable().describe('null = built-in, available in every workspace'),
  slug: z.string(),
  name: z.string(),
  kind: z.enum(['service', 'vendor']),
  builtIn: z.boolean(),
  description: z.string().default(''),
  baseUrls: z.record(Environment, z.string()),
  auth: z.object({ kind: AuthKind, header: z.string().optional(), credentialName: z.string().optional() }),
  request: z.object({
    method: HttpMethod,
    path: z.string(),
    fields: z.array(PresetField).default([]),
  }),
  replyFields: z.array(z.string()).default([]),
  timeoutMs: z.number().int().min(100),
  retry: RetryRule,
  outcomes: z.array(z.string()).min(1),
  outcomeMap: OutcomeMap,
  sampleReplies: z
    .array(z.object({ name: z.string(), status: z.number().int(), body: z.record(z.string(), z.unknown()) }))
    .default([])
    .describe('fixture replies used by the connector test'),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
})
export type ConnectorPreset = z.infer<typeof ConnectorPreset>

export const ConnectorPresetInput = ConnectorPreset.omit({
  id: true,
  workspaceId: true,
  builtIn: true,
  createdAt: true,
  updatedAt: true,
})
export type ConnectorPresetInput = z.infer<typeof ConnectorPresetInput>

export const ConnectorTestRequest = z.object({
  presetId: z.string(),
  environment: Environment,
  sampleReplyName: z.string().optional(),
  input: z.record(z.string(), z.unknown()).default({}),
})
export type ConnectorTestRequest = z.infer<typeof ConnectorTestRequest>

export const ConnectorTestResult = z.object({
  outcome: z.string(),
  status: z.number().int(),
  durationMs: z.number().int(),
  request: z.object({ method: HttpMethod, url: z.string(), body: z.record(z.string(), z.unknown()) }),
  reply: z.record(z.string(), z.unknown()),
  matchedRule: OutcomeRule.nullable(),
})
export type ConnectorTestResult = z.infer<typeof ConnectorTestResult>

/** A credential: only metadata leaves the vault. The secret is shown once when it is made. */
export const Credential = z.object({
  id: z.string(),
  workspaceId: z.string(),
  presetId: z.string(),
  name: z.string(),
  environment: Environment,
  hint: z.string().describe('last 4 characters of the secret'),
  createdAt: IsoDateTime,
  createdBy: z.string(),
  replacedAt: IsoDateTime.nullable(),
})
export type Credential = z.infer<typeof Credential>

export const CredentialInput = z.object({
  presetId: z.string(),
  name: z.string().min(1),
  environment: Environment,
  secret: z.string().min(8),
})
export type CredentialInput = z.infer<typeof CredentialInput>

export const CredentialCreated = z.object({ credential: Credential, secretShownOnce: z.string() })
export type CredentialCreated = z.infer<typeof CredentialCreated>
